import * as yup from 'yup';
import { Message } from 'yup/lib/types';
import { DocRequestTemplateCategoryType } from 'types/DocRequestTemplate';
import { ProcessOperationType } from 'types/Process';
import { isValidDateValidator } from './date';

const validPassword = ( t: ( arg0: string ) => Message<{}> ) => yup
  .string()
  .required()
  .test( 'minimum_letter', t( 'common.password_minimum_letter' ), ( password?: string ) => !password || password.length >= 8 )
  .test( 'minimum_special', t( 'common.password_minimum_special' ), ( password?: string ) => !password || /[!@#$%^&*+-.]/.test( password ) )
  .test( 'minimum_number', t( 'common.password_minimum_number' ), ( password?: string ) => !password || /(\D*\d){2,}/.test( password ) )
  .test( 'minimum_uppercase', t( 'common.password_minimum_uppercase' ), ( password?: string ) => !password || /[A-Z]/.test( password ) )
  .test( 'minimum_lowercase', t( 'common.password_minimum_lowercase' ), ( password?: string ) => !password || /[a-z]/.test( password ) );

export const yupChangePassword = ( t: any ) => {
  const schemaObj: any = {};
  schemaObj.currentPassword = yup.string().required();
  schemaObj.password = validPassword( t );
  schemaObj.confirmPassword = yup
    .mixed()
    .required( )
    .oneOf( [yup.ref( 'password' )], t( 'common.equal_password' ) );

  return schemaObj;
};

export const yupForcePassword = ( t: any ) => {
  const schemaObj: any = {};
  schemaObj.password = validPassword( t );
  schemaObj.confirmPassword = yup
    .mixed()
    .required( )
    .oneOf( [yup.ref( 'password' )], t( 'common.equal_password' ) );

  return schemaObj;
};

export const yupChangeOldPassword = ( t: any ) => {
  const schemaObj: any = {};
  schemaObj.password = validPassword( t );
  schemaObj.confirmPassword = yup
    .mixed()
    .required( )
    .oneOf( [yup.ref( 'password' )], t( 'common.equal_password' ) );

  return schemaObj;
};

export const yupEditData = () => {
  const schemaObj: any = { fullName: null, email: null };
  // @ts-ignore
  schemaObj.fullName = validName();
  // @ts-ignore
  schemaObj.email = validEmail();
  return { schemaObj };
};

const isMaskedEmpty = ( value?: string ) => !value || value === '__/__/____';

const parseMaskedDate = ( value: string ) => {
  if ( isMaskedEmpty( value ) ) return null;

  // Debe estar completa
  if ( !/^\d{2}\/\d{2}\/\d{4}$/.test( value ) ) return undefined;

  const [day, month, year] = value.split( '/' ).map( Number );
  const date = new Date( year, month - 1, day );

  // Validación fuerte
  if (
    date.getFullYear() !== year
    || date.getMonth() !== month - 1
    || date.getDate() !== day
  ) {
    return undefined;
  }

  return date;
};

export const yupCreateInvitation = () => (
  {
    type: yup.string().oneOf( ['PERSONAL', 'OPORTUNIDAD'] ).default( 'PERSONAL' ).required(),
    emailSender: yup.string()
      .when( 'type', {
        is: 'PERSONAL',
        then: ( s ) => s.required( 'Selecciona un remitente' ),
        otherwise: ( s ) => s.strip(), // no lo envíes si no aplica
      } ),
    emails: yup.array()
      .of( yup.string().email( 'Email inválido' ) )
      .when( 'type', {
        is: 'PERSONAL',
        then: ( s ) => s.min( 1, 'Debes añadir al menos un email' ),
        otherwise: ( s ) => s.optional().default( [] ),
      } ),
    carpetasPaths: yup.array()
      .of( yup.string().required() )
      .min( 1, 'Selecciona al menos un documento' ),
    oportunidadId: yup.number()
      .when( 'type', {
        is: 'OPORTUNIDAD',
        then: ( s ) => s.typeError( 'Selecciona una oportunidad' ).required( 'Selecciona una oportunidad' ),
        otherwise: ( s ) => s.strip(), // no lo envíes si no aplica
      } ),
    fechaExpiracionEnlace: yup
      .string()
      .test(
        'future-masked-date',
        'La fecha debe ser hoy o posterior',
        ( value ) => {
          if ( isMaskedEmpty( value ) ) return true;

          const date = parseMaskedDate( value! );
          if ( !date ) return false;

          const today = new Date();
          today.setHours( 0, 0, 0, 0 );
          date.setHours( 0, 0, 0, 0 );

          return date >= today;
        },
      )
      .test(
        'valid-masked-date',
        'La fecha no es válida (dd/mm/yyyy)',
        ( value ) => {
          if ( isMaskedEmpty( value ) ) return true;

          return Boolean( parseMaskedDate( value! ) );
        },
      ),

  }
);

const timeRegex = /^([01]\d|2[0-3]):([0-5]\d)$/;
const dateRegex = /^(0[1-9]|[12][0-9]|3[01])[/](0[1-9]|1[0-2])[/](19|20)\d{2}$/;

function parseDateTime( date: string, time: string ) {
  const [day, month, year] = date.split( '/' );
  return new Date( `${year}-${month}-${day}T${time}` );
}

export const yupCreateEvent = yup.object( {
  // emails: yup.array().of( yup.string().email() ).min( 1, 'Debes añadir al menos un email' ),
  titulo: yup.string().required(),
  promocionId: yup.string().required(),
  allDate: yup.boolean(),
  fechaInicio: yup.string().required()
    .matches( dateRegex, 'Fecha inválida' )
    .test( 'is-valid-date', 'Fecha inválida', ( value ) => {
      if ( !value ) return true;
      if ( value.includes( '_' ) ) return false;

      const [day, month, year] = value.split( '/' ).map( Number );
      if ( !day || !month || !year ) return false;

      const date = new Date( year, month - 1, day );

      return (
        date.getFullYear() === year
        && date.getMonth() === month - 1
        && date.getDate() === day
      );
    } ),
  horaInicio: yup.string()
    .when( 'allDate', {
      is: false,
      then: yup.string().required(),
    } )
    .matches( timeRegex, 'Hora inválida' ),
  fechaFin: yup.string()
    .when( 'allDate', {
      is: false,
      then: yup.string().required(),
    } )
    .matches( dateRegex, 'Fecha inválida' )
    .test( 'is-valid-date', 'Fecha inválida', ( value ) => {
      if ( !value ) return true;
      if ( value.includes( '_' ) ) return false;

      const [day, month, year] = value.split( '/' ).map( Number );
      if ( !day || !month || !year ) return false;

      const date = new Date( year, month - 1, day );

      return (
        date.getFullYear() === year
        && date.getMonth() === month - 1
        && date.getDate() === day
      );
    } )
    .test(
      'compare-dates',
      'La fecha y hora de fin debe ser mayor que fecha y hora de inicio',
      function compareEventEndAfterStart( this: yup.TestContext<any>, value ) {
        const parent = this?.parent as any | undefined;

        if ( !parent ) return true;
        const { fechaInicio, horaInicio, horaFin } = parent;
        if ( !fechaInicio || !horaInicio || !horaFin || !value ) return true;

        const inicio = parseDateTime( fechaInicio, horaInicio );
        const fin = parseDateTime( value, horaFin );

        return fin > inicio;
      },
    ),
  horaFin: yup.string()
    .when( 'allDate', {
      is: false,
      then: yup.string().required(),
    } )
    .matches( timeRegex, 'Hora inválida' ),
} );

export const yupCreateDocRequestTemplate = yup.object( {
  nombre: yup.string().required(),
} );

export const yupCreateDocRequest = ( category: DocRequestTemplateCategoryType ) => yup.object( {
  nombre: yup.string().required(),
  contactoID: category === DocRequestTemplateCategoryType.physical
    ? yup.string().required( 'Requerido' )
    : yup.string().notRequired(),
  fechaLimite: yup.string().required()
    .matches( dateRegex, 'Fecha inválida' )
    .test( 'is-valid-date', 'Fecha inválida', isValidDateValidator ),
  fechaExpiracion: yup.string()
    .matches( dateRegex, 'Fecha inválida' )
    .test( 'is-valid-date', 'Fecha inválida', isValidDateValidator ),
} );

export const yupUploadDoc = yup.object( {
  file: yup
    .mixed()
    .required( 'El archivo es obligatorio' ),
  // .test( 'fileExists', 'Debes seleccionar un archivo', ( value ) => value instanceof File ),
} );

export const yupCreateProcess = yup.object( {
  operationType: yup.string().required(),
  promotionId: yup.string().required(),
  repartoCapitalSubtype: yup.string().when( 'operationType', {
    is: ProcessOperationType.REPARTO_CAPITAL,
    then: ( schema ) => schema.required(),
    otherwise: ( schema ) => schema.notRequired(),
  } ),
} );

const EXCEL_MIME_TYPES = [
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
  'application/vnd.ms-excel', // .xls
];
const EXCEL_EXT_REGEX = /\.(xlsx|xls)$/i;

export const yupProcessUploadFile = yup.object( {
  uploadFile: yup
    .mixed()
    .required()
    .test(
      'excel',
      'El archivo debe ser Excel (.xlsx o .xls)',
      ( value ) => {
        if ( value == null ) return false;
        const file = value as File;
        const mime = file.type?.toLowerCase() ?? '';
        const name = file.name?.toLowerCase() ?? '';
        const validMime = EXCEL_MIME_TYPES.some( ( t ) => mime === t );
        const validExt = EXCEL_EXT_REGEX.test( name );
        return validMime || validExt;
      },
    ),
} );

export const yupProcessStep5Contacts = yup.object( {
  emailSender: yup
    .string()
    .nullable()
    .required(),
  ccEmailsManual: yup
    .array()
    .of( yup.string().email( 'Email inválido' ) )
    .default( [] ),
  toContactTypes: yup
    .array()
    .required()
    .nullable()
    .min( 1, 'form.requiredField' ),
} );

export const yupDocNote = yup.object( {} );

/** Login SSO: correo en `identifier` (equiv. `loginValidatorEmailOnly` en cliente). */
export const yupLoginSsoIdentifier = yup.object( {
  identifier: yup
    .string()
    .trim()
    .required( 'El correo electrónico es obligatorio.' )
    .email( 'Introduce un correo electrónico válido.' ),
} );

/** Login SSO: OTP en campo `otpCode` (mismo criterio que `otpSchema` del cliente). */
export const yupLoginSsoOtpCode = yup.object( {
  otpCode: yup
    .string()
    .trim()
    .required( 'El código OTP es requerido.' )
    .length( 6, 'El código debe tener exactamente 6 caracteres.' )
    .matches( /^[a-zA-Z0-9]{6}$/, 'El código debe contener solo letras y números.' ),
} );
